import React, { useEffect } from 'react'
import CustomCheckbox from 'designer/component/element/Checkbox'
import objectPath from 'object-path'
import { copyDataByCondition } from 'designer/component/action/Condition'
import { getCheckValue } from 'designer/component/action/Data'
import { onUpdateCondition } from 'designer/component/action/condition/Action'
import { debounceCallback } from 'utility/debounceCallback.util'
import { getStorage } from 'designer/component/action/Storage'
import { isEmpty } from 'designer/component/action/condition/State'
import PropWrapper from './PropWrapper'
import {
  updateFieldValue,
  updateMapResponse,
  updateOnChange,
  updateWidgetData,
} from './DataControl'

const findUiByActionComponent = (component = {}, elements = []) => {
  let { fieldName, name, title } = component
  let targetName = fieldName || title || name
  if (!targetName) return

  for (let element of elements) {
    if (
      element.fieldName === targetName ||
      element.title === targetName ||
      element.name === targetName
    ) {
      return element
    }

    let foundElement = findUiByActionComponent(
      component,
      element.elements || [],
    )
    if (foundElement) return foundElement
  }
}

const getActionComponentKey = (component = {}, designContext = {}) => {
  let { jsonForm, getCurrentUi } = designContext
  let currentUi = component.key ? getCurrentUi(component.key) : null
  if (currentUi?.key === component.key) return component.key

  let foundUi = findUiByActionComponent(
    component,
    jsonForm?.uischema?.elements || [],
  )
  return foundUi?.key || component.key
}

const Children = ({ dataProps }) => {
  const {
    props,
    designContext,
    fieldProps,
    fieldName,
    fieldKey,
    errors,
    setIsOnChange,
  } = dataProps
  const {
    jsonForm,
    setFormData,
    isResetData,
    elementAction,
    setElementAction,
  } = designContext
  const { uischema } = props
  const { label } = uischema

  const formData = jsonForm.formData
  let {
    uiKey,
    uiName,
    value,
    data,
    renderWidget,
    mapResponse,
    defaultChecked,
  } = fieldProps
  let valueChecked = getCheckValue(uischema.valueChecked)
  let valueUnchecked = getCheckValue(uischema.valueUnchecked)

  const getIsChecked = (value) => getCheckValue(value) === valueChecked

  const checkCheckboxAction = (checked = false) => {
    let { action = {} } = uischema
    let actionName = action?.name
    let { component = [] } = action
    if (!actionName) return
    let actionValue = objectPath.get(action, actionName)
    let elementActionKey
    let elementActionValue
    if (actionName === 'readonly') {
      elementActionKey = 'isEnable'
      elementActionValue = actionValue !== checked
    } else if (actionName === 'showHide') {
      actionValue = objectPath.has(action, 'isShowUi')
        ? action.isShowUi
        : actionValue
      actionValue = getCheckValue(actionValue)
      elementActionKey = 'isShow'
      elementActionValue = actionValue === checked
    }
    if (elementActionKey) {
      component.forEach((item) => {
        let key = getActionComponentKey(item, designContext)
        if (!key) return

        objectPath.set(
          elementAction,
          [elementActionKey, key],
          elementActionValue,
        )
      })
      setElementAction(elementAction)
    }
  }

  const updateCheckbox = (checked, isOnChange) => {
    let value = checked ? valueChecked : valueUnchecked
    updateData(value, isOnChange)
    checkCheckboxAction(checked)
  }

  const updateData = (value, isOnChange = false) => {
    updateFieldValue({ ...dataProps, value, isOnChange })
  }

  const getDefaultChecked = () => {
    return (
      defaultChecked === true || getCheckValue(defaultChecked) === valueChecked
    )
  }

  const copyData = (checked) => {
    let value = checked ? valueChecked : valueUnchecked
    let newFormData = copyDataByCondition(value, elementAction, uiKey, formData)
    setFormData(newFormData)
  }

  useEffect(() => {
    if (value === undefined || value === null) return
    checkCheckboxAction(getIsChecked(value))
  }, [value])

  useEffect(() => {
    updateOnChange({ ...dataProps, value })
  }, [value])

  useEffect(() => {
    if (value === undefined || value === null || isResetData) {
      let storage = getStorage(jsonForm, uiName)
      if (isEmpty(storage)) {
        updateCheckbox(getDefaultChecked())
      } else {
        updateData(storage)
      }
    }
  }, [defaultChecked, isResetData])

  useEffect(() => {
    updateMapResponse(dataProps)
  }, [mapResponse])

  useEffect(() => {
    updateWidgetData(dataProps)
  }, [renderWidget])

  return (
    <CustomCheckbox
      fieldName={fieldName}
      fieldKey={fieldKey}
      uischema={uischema}
      value={value}
      updateCheckbox={updateCheckbox}
      copyData={copyData}
      errors={errors}
      label={label}
      setIsOnChange={setIsOnChange}
      data={data}
    />
  )
}

const CheckboxControl = (props) => {
  return (
    <PropWrapper {...props}>
      {({ dataProps }) => <Children dataProps={dataProps} />}
    </PropWrapper>
  )
}

export default CheckboxControl
